import * as React from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography,
  Chip, Button, Divider, Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import {useSnackbar} from 'notistack'
import {alertStripSx, primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, SUCCESS, WARNING, ERROR, withOpacity} from '../../../styles/foundation/colors'
import {fmtCurrency, fmtKg, EXCESS_NOTE} from './format'
import {evidenceScore} from './state/evidenceScore'

// Same tone vocabulary as DisputeGrid.js's EVIDENCE_TONE / DisputeDrawer.js's
// EVIDENCE_COLOR — kept local since nothing outside evidence chips needs it.
const EVIDENCE_COLOR = {none: ERROR, weak: WARNING, strong: SUCCESS}

const chipSx = (color) => ({
  bgcolor: withOpacity(color, 0.12),
  color,
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  height: 22,
})

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * ── Pure evidence-application logic ─────────────────────────────────────
 * Everything in this section is a plain function: no React, no dialog, no
 * toast, no I/O. That mirrors actions.js's applyTransition — it is what
 * EvidenceStudio.test.js exercises directly, so the bulk-mutation rules can
 * be verified without mounting a component.
 */

/**
 * Applies one pending evidence payload — `{addImages, addVideo}`, the
 * counts/flag the merchant attached in the studio's upload area for this
 * batch — to a single row. Returns a brand-new row object; never mutates
 * `row`.
 *
 * Video and images are additive, never destructive: attaching video does
 * not clear previously-attached images, and attaching more images on top of
 * an existing video does not un-set `hasVideo`. This matches evidenceScore's
 * own rule (a video always wins) and lets a merchant top up evidence over
 * multiple Evidence Studio visits without losing earlier proof.
 *
 * MUTATIONS ARE LOCAL-ONLY: this function does not call an API and nothing
 * it returns is persisted anywhere — see index.js, where its result is
 * layered over the local `disputes` React state exactly like actions.js's
 * applyTransition. A page reload discards every change made here.
 */
export function applyEvidenceToRow(row, pending) {
  if (!row) throw new Error('applyEvidenceToRow requires a row')
  const addImages = pending?.addImages || 0
  const addVideo = !!pending?.addVideo
  return {
    ...row,
    merchantImages: (row.merchantImages || 0) + addImages,
    hasVideo: row.hasVideo || addVideo,
  }
}

/**
 * Applies `pending` to every row whose id is in `recordIds`, leaving every
 * other row untouched (same object reference, not just equal value).
 *
 * SAFETY CONTRACT: `recordIds` MUST already be the caller's *effective*
 * selection — see index.js's `effectiveSelection` (raw selection intersected
 * with the rows currently matching the active filters). This function has no
 * way to know which ids are "supposed" to be selected; it trusts its caller
 * completely, which is exactly why index.js wires it to `effectiveSelection`
 * and never the raw `selection` Set. Passing the raw selection here would
 * apply evidence to — and report success for — rows the merchant filtered
 * away and never saw, the same class of bug Task 6 fixed for the grid's
 * selection banner.
 */
export function applyEvidenceToRows(rows, recordIds, pending) {
  const idSet = new Set(recordIds)
  return rows.map((row) => (idSet.has(row.id) ? applyEvidenceToRow(row, pending) : row))
}

/** How many of `records` currently score `strong` per evidenceScore(). */
export function countStrong(records) {
  return records.reduce((n, r) => n + (evidenceScore(r).level === 'strong' ? 1 : 0), 0)
}

/**
 * Full-screen bulk evidence capture — mirrors the mockup's
 * `PZ.evidence.open(recordIds)`.
 *
 * `recordIds` MUST be the caller's effective selection (see
 * applyEvidenceToRows's safety contract above and index.js's
 * `effectiveSelection`). This component does no filtering of its own beyond
 * "is this id in the list I was given" — it lists and mutates exactly the
 * ids it is handed, nothing more.
 *
 * `rows` is the live, mutable dispute list (index.js's `disputes` state —
 * NOT the frozen `DISPUTES` export). Evidence attached on an earlier
 * Evidence Studio visit, or a status change from actions.js, must be
 * reflected here, and the frozen export never carries either.
 *
 * `onSubmit(recordIds, pending)` is how the caller applies the change to its
 * own state (see index.js, which calls `applyEvidenceToRows` with these
 * exact arguments) — this component does not touch `rows` itself.
 */
export default function EvidenceStudio({recordIds, rows, open, onClose, onSubmit}) {
  const {enqueueSnackbar} = useSnackbar()
  const [addImages, setAddImages] = React.useState(0)
  const [addVideo, setAddVideo] = React.useState(false)

  // One-shot submit guard. `open` only flips to `false` on the *next* render
  // (via onClose()), so a rapid double-click or double Enter-press can
  // re-enter handleSubmit before that commits. A ref is checked/set
  // synchronously — unlike state, it can't leave a window where a second
  // invocation in the same tick still sees the pre-submit values — so
  // onSubmit (and therefore applyEvidenceToRow's non-idempotent image count)
  // can never fire twice for one visit. hasSubmitted mirrors it in state
  // purely to keep the Submit button visually disabled.
  const submittedRef = React.useRef(false)
  const [hasSubmitted, setHasSubmitted] = React.useState(false)

  // Pending evidence — and the submit guard — are scoped to one studio visit:
  // reset whenever it opens so evidence staged for a previous batch (or a
  // previous, now-closed selection) can never leak into the next one, and so
  // the guard doesn't permanently latch across visits.
  React.useEffect(() => {
    if (open) {
      setAddImages(0)
      setAddVideo(false)
      submittedRef.current = false
      setHasSubmitted(false)
    }
  }, [open])

  const records = React.useMemo(() => {
    const idSet = new Set(recordIds)
    return rows.filter((r) => idSet.has(r.id))
  }, [rows, recordIds])

  const pending = React.useMemo(() => ({addImages, addVideo}), [addImages, addVideo])

  // Live preview only — never written back to `rows`. Drives both the
  // per-record chips below and the running "N of M strong" summary as the
  // merchant attaches evidence, before Submit commits anything.
  const previewRecords = React.useMemo(
    () => records.map((r) => applyEvidenceToRow(r, pending)),
    [records, pending],
  )

  const total = records.length
  const strongCount = countStrong(previewRecords)
  const hasPendingEvidence = addImages > 0 || addVideo

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) setAddVideo(true)
    e.target.value = ''
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) setAddImages((n) => n + files.length)
    e.target.value = ''
  }

  const handleSubmit = () => {
    if (submittedRef.current || !hasPendingEvidence || total === 0) return
    submittedRef.current = true
    setHasSubmitted(true)
    onSubmit(recordIds, pending)
    enqueueSnackbar(`Evidence attached to ${total} record${total === 1 ? '' : 's'}`, {variant: 'success'})
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* fontSize is pinned rather than inherited: MuiDialogTitle carries its
          own size from the app theme, which nothing here can reach, and this is
          the title of a panel — it has to match the drawers' own. */}
      <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md}}>
        Evidence Studio
        <IconButton onClick={onClose} aria-label='Close evidence studio' size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{display: 'flex', gap: 3, flexWrap: {xs: 'wrap', md: 'nowrap'}}}>
        <Box sx={{flex: '1 1 380px', maxWidth: {md: 420}}}>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1.5}}>
            {total} record{total === 1 ? '' : 's'} selected
          </Typography>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, maxHeight: {md: '70vh'}, overflowY: 'auto', pr: 0.5}}>
            {previewRecords.map((record) => {
              const evidence = evidenceScore(record)
              const sameAsSku = !record.product || record.product === record.sku
              return (
                <Box key={record.id} sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5}}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
                    <Box>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                        {record.awb}
                      </Typography>
                      <Typography sx={labelSx}>{sameAsSku ? record.sku : `${record.sku} · ${record.product}`}</Typography>
                    </Box>
                    <Chip size='small' label={evidence.label} sx={chipSx(EVIDENCE_COLOR[evidence.level])} />
                  </Box>
                  <Tooltip title={EXCESS_NOTE}>
                    <Typography sx={{...labelSx, mt: 0.5, width: 'fit-content'}}>
                      +{fmtKg(record.excessKg)} · {fmtCurrency(record.excessAmount)}
                    </Typography>
                  </Tooltip>
                </Box>
              )
            })}
          </Box>
        </Box>

        <Divider orientation='vertical' flexItem sx={{display: {xs: 'none', md: 'block'}}} />

        <Box sx={{flex: '1 1 420px'}}>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1.5}}>
            Attach evidence to all {total} selected record{total === 1 ? '' : 's'}
          </Typography>

          {/* Video is deliberately the dominant, first-seen option — Prozo's
              published guidance (see state/evidenceScore.js) is that a
              complete packing video is the strongest evidence a merchant can
              submit, images alone are weaker, and a claim with no
              outer-packaging visual at all is rejected outright. Phase C6
              dropped the specific win-rate percentages the copy below used
              to quote — the ranking survives, the numbers don't. The larger
              card, bolder copy and accent border below are intentional, not
              decoration — they push merchants toward video instead of
              treating the two as equivalent. The emphasis is carried by the
              accent border, the tint, the icon and the weight — NOT by a larger
              type size, which would put a card title above the section heading
              it sits under. */}
          <Box sx={{
            border: `2px dashed ${SUCCESS}`, borderRadius: '12px', p: 3, textAlign: 'center',
            bgcolor: withOpacity(SUCCESS, 0.06), mb: 2,
          }}
          >
            <VideocamOutlinedIcon sx={{fontSize: 36, color: SUCCESS}} />
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mt: 1}}>
              Packing video — the strongest evidence you can submit
            </Typography>
            <Typography sx={{...labelSx, mb: 1.5, maxWidth: 380, mx: 'auto'}}>
              Record the complete unboxing/packing before dispatch. This is the strongest evidence a merchant can submit.
            </Typography>
            <Button component='label' variant='contained' sx={primaryBtnSx}>
              {addVideo ? 'Video attached ✓' : 'Attach packing video'}
              <input type='file' hidden accept='video/*' onChange={handleVideoChange} aria-label='Attach packing video' />
            </Button>
          </Box>

          <Box sx={{border: `1px dashed ${BORDER}`, borderRadius: '12px', p: 2, textAlign: 'center', mb: 2}}>
            <ImageOutlinedIcon sx={{fontSize: 22, color: SUBTLE}} />
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY, mt: 0.5}}>
              Images only — weaker than video
            </Typography>
            <Typography sx={{...labelSx, mb: 1}}>
              Use only when a video isn&apos;t available. A claim with no outer-packaging visual at all is rejected outright.
            </Typography>
            <Button component='label' variant='outlined' size='small' sx={secondaryBtnSx}>
              {addImages > 0 ? `${addImages} image(s) attached` : 'Attach images'}
              <input type='file' hidden accept='image/*' multiple onChange={handleImageChange} aria-label='Attach images' />
            </Button>
          </Box>

          <Box sx={alertStripSx(strongCount === total && total > 0 ? SUCCESS : WARNING)}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>
              {strongCount} of {total} record{total === 1 ? '' : 's'} now have strong evidence
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{p: 2}}>
        <Button onClick={onClose} sx={secondaryBtnSx}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!hasPendingEvidence || total === 0 || hasSubmitted} variant='contained' sx={primaryBtnSx}>
          Apply evidence
        </Button>
      </DialogActions>
    </Dialog>
  )
}
